// Sticky add-to-cart bar.
// Product mode (default): slides up once the main Add to cart button scrolls out of view and presses
// that real submit button, so variant, quantity and the cart drawer flow stay owned by <s-product-form>.
// Watch mode (data-watch="selector"): slides up once the watched element scrolls above the viewport;
// the bar carries its own add-to-cart forms (used on the bundles page).
class SStickyAtc extends HTMLElement {
  #abort = null;
  #observer = null;
  #target = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#setVisible(false);

    this.querySelector('[data-sticky-atc-button]')?.addEventListener(
      'click',
      () => {
        const realButton = this.#findRealButton();
        if (realButton) realButton.click();
      },
      { signal },
    );

    this.#observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      const ready = this.dataset.watch ? true : Boolean(this.#findRealButton());
      this.#setVisible(scrolledPast && ready);
    });
    this.#watch();

    if (!this.dataset.watch) {
      // The product-form partial is re-rendered on variant change; re-attach to the new button and refresh the price.
      const form = document.querySelector('[data-product-form]');
      if (form) {
        const mutation = new MutationObserver(() => this.#watch());
        mutation.observe(form, { childList: true, subtree: true });
        signal.addEventListener('abort', () => mutation.disconnect());
      }
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #findRealButton() {
    return document.querySelector('[data-product-form] button[type="submit"][name="add"]');
  }

  #watch() {
    const target = this.dataset.watch
      ? document.querySelector(this.dataset.watch)
      : (this.#findRealButton() ?? document.querySelector('[data-product-form]'));
    if (target && target !== this.#target) {
      if (this.#target) this.#observer.unobserve(this.#target);
      this.#observer.observe(target);
      this.#target = target;
    }
    if (this.dataset.watch) return;
    const source = document.querySelector('[data-product-price]');
    const price = this.querySelector('[data-sticky-atc-price]');
    if (source && price) price.innerHTML = source.innerHTML;
  }

  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
  }
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
